"use client"

import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react"
import type Lenis from "lenis"

type ContextValue = {
  setLenis: (lenis: InstanceType<typeof Lenis>) => void
  lenis: InstanceType<typeof Lenis> | undefined
}

const ScrollContext = createContext<ContextValue | undefined>(undefined)

type ProviderProps = {
  children: ReactNode
}

export const ScrollProvider = ({ children }: ProviderProps) => {
  const [lenis, setLenis] = useState<InstanceType<typeof Lenis> | undefined>(
    undefined,
  )

  const contextValue: ContextValue = useMemo(
    () => ({
      lenis,
      setLenis,
    }),
    [lenis, setLenis],
  )

  return (
    <ScrollContext.Provider value={contextValue}>
      {children}
    </ScrollContext.Provider>
  )
}

/** Hook to access the 'ScrollProvider' context. */
export const useScroll = () => {
  const contextValue = useContext<ContextValue | undefined>(ScrollContext)

  if (!contextValue) {
    throw new Error(
      "'useScroll' must be used in a component with a parent 'ScrollProvider'.",
    )
  }

  return contextValue
}
